/**
 * Ereignistypen und die Form eines Ereignisses (§56, §57).
 *
 * **Warum die Liste hier liegt:** sie wird an drei Stellen gebraucht — als
 * `CHECK`-Bedingung im Datenmodell, als Laufzeitprüfung im Repository und als Typ im
 * Compiler. Drei Listen wären drei Wahrheiten, von denen zwei veralten können; dieses Paket
 * ist die einzige Quelle für Datenformate (`ARCHITECTURE.md` E4), also gehört sie hierher.
 *
 * **Der Unterschied zwischen permanent und transient ist eine Regel, keine Technik.**
 * Permanente Ereignisse bilden die Zughistorie (§56) und sind ab V3 die Balancing-Daten.
 * Preview-Ereignisse (§57) sind in Echtzeit sichtbar, aber **absichtlich nicht
 * rekonstruierbar** (§41): man sieht live, dass jemand drei Züge erwägt, kann später aber
 * nicht nachlesen, welche das waren. Nur der Debug-/Analysemodus darf sie protokollieren —
 * und dann nicht in der Ereignistabelle.
 *
 * **Die Form des `payload` je Typ ist hier offen.** §56 gibt ein Beispiel für `MOVE`
 * (`pieceId`, `from`, `to`), aber was ein `PUSH` oder ein `SCORE` mitführt, entscheidet der
 * Commit-Ablauf in Phase 7. Ein jetzt geratenes Schema je Typ wäre eine Erfindung, die
 * später jede Aufrufstelle berührt. Geprüft wird deshalb die Hülle — Typ, Zugnummer,
 * Spieler — und dass der Inhalt JSON-fähig ist.
 *
 * `timestamp` aus dem Beispiel in §56 ist **nicht** Teil der Eingabe: die Zeit setzt die
 * Datenbank (`created_at`), damit App- und Datenbankuhr nicht auseinanderlaufen können.
 */
import { z } from 'zod';
/**
 * Die permanenten Ereignistypen: die zehn aus §56, dazu der Debug-Eingriff (**E42**).
 *
 * Reihenfolge wie in §56, damit sich die Liste gegen die Quelle lesen lässt — der elfte steht
 * deshalb am Ende und nicht alphabetisch einsortiert.
 *
 * **`DEBUG_INTERVENTION` steht nicht in §56, und das ist Absicht.** Ein Eingriff ist kein Zug
 * (Nr. 37): er hat keinen Spieler, rückt die Reihenfolge nicht weiter und soll in keiner
 * Auswertung als Zug gezählt werden. Ihn unter `MOVE` zu führen wäre bequem und genau deshalb
 * falsch — die Historie würde „Anna zieht Wächter nach green-C3" zeigen, obwohl Anna nichts
 * getan hat. Die ausführliche Begründung samt der verworfenen Alternativen steht in
 * `docs/ARCHITECTURE.md` **E42**.
 *
 * **Permanent und nicht transient**, obwohl ein Eingriff kein Spielzug ist: wer zehn Minuten
 * später eine seltsame Stellung sieht, muss sie erklärt bekommen (Frage 2 zu §60). Ein
 * flüchtiger Hinweis wäre nach einem Neustart weg — und dann bliebe die Stellung unerklärt.
 */
export const PERMANENT_EVENT_TYPES = [
    'MOVE',
    'CAPTURE',
    'PUSH',
    'RESPAWN',
    'SCORE',
    'FLAG_CAPTURE',
    'AUTO_SETUP',
    'TIMEOUT',
    'GAME_STARTED',
    'GAME_FINISHED',
    'DEBUG_INTERVENTION',
];
/** Die drei transienten Preview-Ereignisse (§57). Nie Teil der permanenten Historie (§41). */
export const TRANSIENT_EVENT_TYPES = [
    'PREVIEW_CREATED',
    'PREVIEW_CHANGED',
    'PREVIEW_CANCELLED',
];
const PERMANENT_EVENT_TYPE_SET = new Set(PERMANENT_EVENT_TYPES);
/**
 * Prüft einen Typ, der als Zeichenkette hereinkommt — aus einer Nachricht, einer Zeile oder
 * einem Debug-Werkzeug.
 *
 * Als Type Guard formuliert, damit der Compiler nach der Prüfung mitträgt, statt dass ein
 * `as` nötig wird.
 */
export function isPermanentEventType(type) {
    return PERMANENT_EVENT_TYPE_SET.has(type);
}
export const permanentEventTypeSchema = z.enum(PERMANENT_EVENT_TYPES);
/**
 * Ein Ereignis, wie es angelegt wird. `seq` und `createdAt` setzt die Datenbank.
 *
 * `strictObject` wie bei allen Schemas dieses Pakets: ein stillschweigend ignoriertes Feld
 * wäre ein Datenverlust, den niemand bemerkt.
 */
export const gameEventInputSchema = z.strictObject({
    type: permanentEventTypeSchema,
    /** Die Zugnummer aus dem GameState. 0 heißt: noch kein Zug geschehen (`GAME_STARTED`). */
    turnNumber: z.number().int().min(0),
    /** `null` bei Ereignissen der Partie selbst — `GAME_STARTED`, `GAME_FINISHED`. */
    player: z.string().min(1).nullable(),
    /** Form je Typ offen bis Phase 7, siehe Modulkopf. Muss JSON-fähig sein. */
    payload: z.record(z.string(), z.unknown()),
});
/** Ein gespeichertes Ereignis — mit der je Partie fortlaufenden Nummer. */
export const gameEventSchema = gameEventInputSchema.extend({
    seq: z.number().int().min(1),
});
//# sourceMappingURL=game-events.js.map